import { useState } from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Checkbox } from '@opendoor/atoms/checkbox';
import { Combobox } from '@opendoor/atoms/combobox';
import { Dialog } from '@opendoor/atoms/dialog';
import { Field } from '@opendoor/atoms/field';
import { Input } from '@opendoor/atoms/input';
import { Text } from '@opendoor/atoms/text';
import { globalObservability } from '@opendoor/observability/slim';
import { useRouter } from 'next/router';

import {
  ADDRESS_INVALID_RESULT,
  gateProceedToOnboardingFlow,
  useAddressValidationContext,
} from '../../helpers/AddressValidationContext';
import type { SelectedAddress } from '../../helpers/AddressValidationContext';
import { useObservability } from '../../helpers/observability';
import { standardizeAddress } from '../api';
import type { OdProtosSellReceptionData_SellerInput_Channel } from '../api/sellReceptionEnums';
import type { AddressSearchLocation, TrackingTaxonomy } from '../globals';
import { addressNotFound, extractStreetNumber } from './AddressConfirmationModal.helpers';

const Sentry = globalObservability.getSentryClient();

type RelationshipValue = 'Self' | 'Agent' | 'AgentAndOwner' | 'other';

const RELATIONSHIP_OPTIONS: Array<{ value: RelationshipValue; label: string }> = [
  { value: 'Self', label: 'Yes, I own this home' },
  { value: 'Agent', label: 'No, I’m an agent' },
  { value: 'AgentAndOwner', label: 'I’m an agent, and own this home' },
  { value: 'other', label: 'Other' },
];

export const AddressConfirmationModal = ({
  isOpen,
  setIsOpen,
  partnerReferralId,
  initialAddress,
  pageSetPath,
  trackingKeywords,
  channel,
  inputLocation,
  trackingTaxonomy,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  partnerReferralId?: string;
  initialAddress: SelectedAddress;
  pageSetPath?: string;
  trackingKeywords?: string;
  channel?: OdProtosSellReceptionData_SellerInput_Channel;
  inputLocation: AddressSearchLocation;
  trackingTaxonomy: TrackingTaxonomy;
}) => {
  const router = useRouter();
  const { trackEvent } = useObservability();
  const { addressResults, setValidatedAddress, validatedAddress } = useAddressValidationContext();
  const [address, setAddress] = useState<SelectedAddress>({ ...initialAddress });
  const [unitNumberErrorText, setUnitNumberErrorText] = useState<string>('');
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [noUnitChecked, setNoUnitChecked] = useState<boolean>(false);
  const [confirmSameUnitStreet, setConfirmSameUnitStreet] = useState<boolean>(false);
  const [relationshipValue, setRelationshipValue] = useState<RelationshipValue | ''>('');
  const [relationshipError, setRelationshipError] = useState<string>('');
  const isAddressUnitNumberMissing = addressResults?.includes('ADDRESS_MISSING_SUB_PREMISE');
  const streetNumber = extractStreetNumber(initialAddress);

  const validateUnitInput = async (): Promise<SelectedAddress | undefined> => {
    try {
      const response = await standardizeAddress(address);
      if (
        !response ||
        !response.data ||
        (response.errors && !addressNotFound(response.errors[0]))
      ) {
        setUnitNumberErrorText('An unexpected error occurred. Please try again.');
        return;
      }
      const casaAddress = response.data?.casa.findByFields?.address;
      if (
        casaAddress?.result.includes(ADDRESS_INVALID_RESULT) ||
        (response.errors && addressNotFound(response.errors[0]))
      ) {
        setUnitNumberErrorText('Unit number not found or entered incorrectly');
        return;
      }
      return {
        street1: casaAddress.street,
        city: casaAddress.city,
        state: casaAddress.state,
        postal_code: casaAddress.postalCode,
        unit: casaAddress.unitNumber,
        id: casaAddress.uuid,
      };
    } catch (error) {
      Sentry.captureException?.(error);
      setUnitNumberErrorText('An unexpected error occurred. Please try again.');
      return;
    }
  };

  const routeToManualAddressPage = () => {
    void router.push({
      pathname: '/manual-address-entry',
      query: {
        address: JSON.stringify(validatedAddress),
        ...(trackingKeywords != null && { trackingKeywords }),
        ...(pageSetPath != null && { pageSetPath }),
        ...(partnerReferralId != null && { partnerReferralId }),
        ...(channel != null && { channel }),
      },
    });
  };

  const proceedWithAddress = async (addressToUse: SelectedAddress) => {
    setValidatedAddress(addressToUse);

    const relationshipAnswers = relationshipValue
      ? { 'seller.relation_to_owner': relationshipValue }
      : undefined;

    try {
      await gateProceedToOnboardingFlow({
        address: addressToUse,
        product: 'SELL_DIRECT',
        trackEvent,
        partnerReferralId,
        addressUUID: addressToUse.id,
        pageSetPath,
        trackingKeywords,
        channel,
        inputLocation,
        trackingTaxonomy,
        relationshipAnswers,
      });
    } catch (error) {
      Sentry.captureException?.(error);
      setUnitNumberErrorText('An unexpected error occurred. Please try again.');
    } finally {
      setIsConfirmLoading(false);
    }
  };

  const validateRelationship = (): boolean => {
    if (!relationshipValue) {
      setRelationshipError('Please select an option');
      return false;
    }
    setRelationshipError('');
    return true;
  };

  const handleRelationshipSelect = (value: RelationshipValue | '') => {
    setRelationshipValue(value);
    setRelationshipError('');
  };

  const handleNoUnitToggle = (checked: boolean) => {
    setNoUnitChecked(checked);
    if (checked) {
      setAddress((prev) => ({ ...prev, unit: '' }));
      setUnitNumberErrorText('');
    }
  };

  const handleConfirm = async () => {
    setIsConfirmLoading(true);

    if (!validateRelationship()) {
      setIsConfirmLoading(false);
      return;
    }

    if (isAddressUnitNumberMissing && !noUnitChecked) {
      if (!address?.unit) {
        setUnitNumberErrorText('Enter a unit number or check "No unit number" below');
        setIsConfirmLoading(false);
        return;
      }

      const validatedAddr = await validateUnitInput();
      if (validatedAddr) {
        await proceedWithAddress(validatedAddr);
      } else {
        setIsConfirmLoading(false);
      }
    } else {
      await proceedWithAddress(address);
    }
  };

  const handleUnitNumberChange = (input: string) => {
    setConfirmSameUnitStreet(false);
    if (address) {
      setAddress({ ...address, unit: input });
    }
    const validUnitNumberRegex = /^[A-Za-z0-9\s-#]+$/;
    if (input && !validUnitNumberRegex.test(input)) {
      setUnitNumberErrorText('Unit numbers can’t include special characters.');
    } else if (input === streetNumber) {
      setUnitNumberErrorText(
        'If street and unit number are the same, tap "Same street and unit" below.',
      );
    } else {
      setUnitNumberErrorText('');
    }
  };

  const selectedRelationshipOption =
    RELATIONSHIP_OPTIONS.find((option) => option.value === relationshipValue) ?? null;

  return (
    <Dialog.Root role="dialog" open={isOpen} onOpenChange={setIsOpen}>
      {/* Match the original's footprint: a full-width bottom sheet on mobile
          (Atoms default) and a centered modal that is ~488px on tablet and
          ~592px from lg up (the prior width=600 / smallerThanLG=100% behavior).
          Width is set on the popup so it includes the dialog's own padding. */}
      <Dialog className="sm:w-122 lg:w-148">
        {/* Match the original's flat 32px title at every breakpoint: Dialog's
            built-in title scales up to text-header-lg (45px) on desktop, so
            render Dialog.Title directly and pin it to text-subheader-lg (32px).
            pb-4/sm:pb-6 + sm:pr-8 mirror the built-in title spacing (keeps the
            heading clear of the top-right close button). */}
        <Dialog.Title className="pb-4 sm:pr-8 sm:pb-6 sm:text-subheader-lg">
          {isAddressUnitNumberMissing ? 'Is there a unit?' : 'Is this the correct home address?'}
        </Dialog.Title>
        <Col className="w-full gap-6">
          <Col className="gap-8 max-lg:gap-6">
            <Row className="w-full items-center justify-between rounded-3xl bg-od-bg-secondary p-6 max-lg:rounded-2xl max-lg:p-4">
              <Col className="w-3/5 max-lg:w-[70%]">
                <Text as="p" className="text-body-xl max-lg:text-body-lg">
                  {address?.street1}
                  {address?.unit ? `, #${address.unit}` : ''}
                </Text>
                <Text as="p" className="text-body-xl max-lg:text-body-lg">
                  {address?.city}, {address?.state} {address?.postal_code}
                </Text>
              </Col>
              {/* Action kept as a real (focusable, analytics-wired) Button, styled
                  like the original TextButton's chrome-less brand text. */}
              <Button
                analyticsName="cosmos-edit-address"
                aria-label="Edit"
                variant="primary"
                className="h-auto w-max border-0 bg-transparent p-0 text-label-lg !text-od-content-accent-brand not-disabled:hover:bg-transparent not-disabled:active:scale-100"
                onClick={routeToManualAddressPage}
              >
                Edit
              </Button>
            </Row>

            {isAddressUnitNumberMissing && (
              <Col className="gap-1.5">
                <Field
                  id="cosmos-address-confirmation-input"
                  label="Add unit number, apt, floor, etc."
                  error={unitNumberErrorText || undefined}
                >
                  <Input
                    id="cosmos-address-confirmation-input"
                    size="base"
                    placeholder="#1234"
                    value={address?.unit ?? ''}
                    disabled={noUnitChecked}
                    error={Boolean(unitNumberErrorText)}
                    onChange={(event) => handleUnitNumberChange(event.target.value)}
                  />
                </Field>

                {address?.unit === streetNumber && !confirmSameUnitStreet && !noUnitChecked && (
                  <Button
                    id="same-street-unit"
                    aria-label="Same street and unit"
                    analyticsName="cosmos-same-street-unit"
                    trackImpression
                    variant="primary"
                    className="h-auto w-max border-0 bg-transparent p-0 text-label-lg !text-od-content-accent-brand not-disabled:hover:bg-transparent not-disabled:active:scale-100"
                    onClick={() => {
                      setUnitNumberErrorText('');
                      setConfirmSameUnitStreet(true);
                    }}
                  >
                    Same street and unit
                  </Button>
                )}

                <Row
                  analyticsName="address-confirmation-no-unit-checkbox"
                  className="items-center gap-2"
                >
                  <Checkbox
                    analyticsName="address-confirmation-no-unit"
                    aria-label="No unit number"
                    value="no-unit"
                    checked={noUnitChecked}
                    onCheckedChange={handleNoUnitToggle}
                  />
                  <Text as="span" className="text-body-md text-od-content-secondary">
                    No unit number
                  </Text>
                </Row>
              </Col>
            )}

            <Field
              id="address-confirmation-relationship"
              label="Are you the owner of this home?"
              error={relationshipError || undefined}
            >
              <Combobox
                id="address-confirmation-relationship"
                analyticsName="address-confirmation-relationship-dropdown"
                options={RELATIONSHIP_OPTIONS}
                value={selectedRelationshipOption}
                placeholder="Select"
                error={Boolean(relationshipError)}
                onValueChange={(option) => {
                  const selected = RELATIONSHIP_OPTIONS.find((o) => o.value === option?.value);
                  handleRelationshipSelect(selected?.value ?? '');
                }}
              />
            </Field>
          </Col>

          <Button
            className="w-full"
            analyticsName="address-confirmation-modal-confirm"
            aria-label="Confirm"
            size="lg"
            variant="primary"
            loading={isConfirmLoading}
            disabled={
              !relationshipValue ||
              !!relationshipError ||
              !!unitNumberErrorText ||
              (isAddressUnitNumberMissing && !address?.unit && !noUnitChecked)
            }
            onClick={handleConfirm}
          >
            Confirm
          </Button>
        </Col>
      </Dialog>
    </Dialog.Root>
  );
};
